import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me, type ModuleSummary } from '../lib/api';
import CompleteProfileBanner from '../components/CompleteProfileBanner';

export default function DashboardPage() {
  const [modules, setModules] = useState<ModuleSummary[]>([]);
  const [me, setMe] = useState<Me['user']>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [modRes, meRes] = await Promise.all([
          api.get<{ modules: ModuleSummary[] }>('/api/curriculum/modules'),
          api.get<Me>('/api/auth/me').catch(() => ({ user: null }) as Me),
        ]);
        setModules(modRes.modules);
        setMe(meRes.user);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    const totalLessons = modules.reduce((acc, m) => acc + m.lessonCount, 0);
    const totalCompleted = modules.reduce((acc, m) => acc + m.completedCount, 0);
    const weeksComplete = modules.filter(
      (m) => m.lessonCount > 0 && m.completedCount === m.lessonCount
    ).length;
    // "Currently on" = first unlocked module where completedCount < lessonCount.
    const currentModule =
      modules.find((m) => m.unlocked && m.completedCount < m.lessonCount) ??
      modules.find((m) => m.unlocked) ??
      null;
    return { totalLessons, totalCompleted, weeksComplete, currentModule };
  }, [modules]);

  return (
    <div className="py-6">
      <CompleteProfileBanner firstName={me?.firstName ?? null} />
      <h1 className="text-3xl font-semibold tracking-tight">Your program</h1>
      <p className="mt-2 text-muted">
        Eight weeks. One unlocks per week from your start date. You can skip weeks and come back later.
      </p>

      {loading ? (
        <div className="mt-10 text-muted">Loading…</div>
      ) : modules.length === 0 ? (
        <div className="mt-10 rounded-md border border-dashed border-line p-6 text-sm text-muted">
          Curriculum is being prepared. Week 1 will appear here shortly.
        </div>
      ) : (
        <>
          <ProgressHero
            totalLessons={totals.totalLessons}
            totalCompleted={totals.totalCompleted}
            weeksComplete={totals.weeksComplete}
            currentModule={totals.currentModule}
          />
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {modules.map((m) => (
              <ModuleCard key={m.id} m={m} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProgressHero({
  totalLessons,
  totalCompleted,
  weeksComplete,
  currentModule,
}: {
  totalLessons: number;
  totalCompleted: number;
  weeksComplete: number;
  currentModule: ModuleSummary | null;
}) {
  const pct = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;
  return (
    <div className="mt-8 rounded-xl border border-line bg-gradient-to-br from-surface via-surface to-elevated p-6">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Program progress
          </p>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-4xl font-semibold tabular-nums text-text">
              {totalCompleted}
            </span>
            <span className="text-lg text-muted">
              of {totalLessons} lessons complete
            </span>
          </div>
          <div className="mt-1 flex items-center gap-4 text-xs text-muted">
            <span>
              <span className="tabular-nums text-text">{weeksComplete}</span>
              {' / 8 weeks finished'}
            </span>
            {currentModule && (
              <>
                <span className="h-3 w-px bg-line" />
                <span>
                  {'Currently on '}
                  <span className="text-text">Week {currentModule.weekNumber}</span>
                </span>
              </>
            )}
          </div>
        </div>

        {currentModule && (
          <Link
            to={`/preview/lesson/${
              currentModule.inProgressLessonSlug ?? `${currentModule.weekNumber}.1`
            }`}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-amber px-5 py-2.5 text-sm font-semibold text-ink hover:bg-amber-hover"
          >
            {currentModule.completedCount > 0 ? 'Resume' : 'Start'}
            {' Lesson '}
            {currentModule.inProgressLessonSlug ?? `${currentModule.weekNumber}.1`}
            {' →'}
          </Link>
        )}
      </div>

      <div className="mt-5">
        <div className="h-2 w-full overflow-hidden rounded-full bg-ink/60">
          <div
            className="h-full rounded-full bg-amber transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 flex justify-between font-mono text-[10px] uppercase tracking-widest text-muted">
          <span>{pct}% complete</span>
          <span>{totalLessons - totalCompleted} lessons to go</span>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ m }: { m: ModuleSummary }) {
  const isComplete = m.lessonCount > 0 && m.completedCount === m.lessonCount;
  const isInProgress = m.unlocked && m.completedCount > 0 && !isComplete;
  const pct =
    m.lessonCount > 0 ? Math.round((m.completedCount / m.lessonCount) * 100) : 0;

  const borderTone = isComplete
    ? 'border-amber/50'
    : isInProgress
    ? 'border-amber/40'
    : m.unlocked
    ? 'border-line'
    : 'border-line/60';

  const bgTone = m.unlocked ? 'bg-surface' : 'bg-surface/40 opacity-70';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-5 transition ${borderTone} ${bgTone}`}
    >
      {/* Top row: week label + status badge */}
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-xs uppercase tracking-widest text-amber">
          Week {m.weekNumber}
        </span>
        <StatusBadge
          isComplete={isComplete}
          isInProgress={isInProgress}
          completedCount={m.completedCount}
          lessonCount={m.lessonCount}
          unlocked={m.unlocked}
        />
      </div>

      <h3 className="mt-2 text-lg font-semibold text-text">{m.title}</h3>
      {m.summary && <p className="mt-1 text-sm text-muted">{m.summary}</p>}

      {/* Progress bar (only when unlocked) */}
      {m.unlocked && m.lessonCount > 0 && (
        <div className="mt-4">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink/60">
            <div
              className={`h-full rounded-full transition-[width] duration-500 ${
                isComplete ? 'bg-amber' : 'bg-amber/80'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Lesson list (unlocked weeks) or unlock hint (locked weeks) */}
      {m.unlocked && m.lessons && m.lessons.length > 0 ? (
        <ul className="mt-4 divide-y divide-line/40 rounded-md border border-line/40">
          {m.lessons.map((l) => (
            <LessonRow key={l.slug} lesson={l} />
          ))}
        </ul>
      ) : m.unlocked ? null : (
        <div className="mt-4 flex items-center justify-between text-xs">
          {m.unlockAt ? (
            <span className="text-muted">
              {'Unlocks '}
              {new Date(m.unlockAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          ) : (
            <span className="text-muted">Locked</span>
          )}
        </div>
      )}
    </div>
  );
}

function LessonRow({ lesson }: { lesson: NonNullable<ModuleSummary['lessons']>[number] }) {
  const iconTone = lesson.completed
    ? 'border-amber bg-amber text-ink'
    : lesson.inProgress
    ? 'border-amber text-amber'
    : lesson.unlocked
    ? 'border-line text-muted'
    : 'border-line/50 text-muted/60';

  const rowBase =
    'flex items-center gap-3 px-3 py-2.5 text-sm transition';
  const rowInteractive = lesson.unlocked
    ? 'text-text hover:bg-elevated'
    : 'text-muted/60 cursor-not-allowed';

  // Extract the .X portion from the slug for the number badge, e.g. "3.4" -> "4".
  const numberInWeek = lesson.slug.includes('.')
    ? lesson.slug.split('.').pop()!
    : String(lesson.orderInModule);

  const inner = (
    <>
      <span
        aria-hidden
        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] ${iconTone}`}
      >
        {lesson.completed ? '✓' : lesson.unlocked ? numberInWeek : '🔒'}
      </span>
      <span className="flex-1 truncate">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
          {'Lesson '}
          {lesson.slug}
        </span>
        <span className="ml-2">{lesson.title}</span>
      </span>
      {lesson.inProgress && (
        <span className="font-mono text-[10px] uppercase tracking-wider text-amber">
          In progress
        </span>
      )}
    </>
  );

  if (lesson.unlocked) {
    return (
      <li>
        <Link
          to={`/preview/lesson/${lesson.slug}`}
          className={`${rowBase} ${rowInteractive}`}
        >
          {inner}
        </Link>
      </li>
    );
  }
  return (
    <li>
      <div
        className={`${rowBase} ${rowInteractive}`}
        aria-disabled="true"
        title="Finish the previous lesson to unlock"
      >
        {inner}
      </div>
    </li>
  );
}

function StatusBadge({
  isComplete,
  isInProgress,
  completedCount,
  lessonCount,
  unlocked,
}: {
  isComplete: boolean;
  isInProgress: boolean;
  completedCount: number;
  lessonCount: number;
  unlocked: boolean;
}) {
  if (isComplete) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber/40 bg-amber/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
        <span aria-hidden>✓</span>
        <span>Complete</span>
      </span>
    );
  }
  if (isInProgress) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/[0.06] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
        <span className="tabular-nums">
          {completedCount}/{lessonCount}
        </span>
      </span>
    );
  }
  if (unlocked) {
    return (
      <span className="rounded-full border border-line px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted">
        <span className="tabular-nums">0/{lessonCount}</span>
      </span>
    );
  }
  return (
    <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
      {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
    </span>
  );
}

